/**
 * useTapSecret — a hidden trigger you can actually reach on a phone.
 *
 * Tap a non-interactive element N times in quick succession and something happens.
 * A key sequence would have been easier but needs a keyboard, which rules out the
 * device most people will find this on.
 *
 * The window resets between taps rather than over the whole run, so an accidental
 * double tap this morning cannot combine with one this afternoon.
 */
import { useCallback, useEffect, useRef } from 'react';

interface Options {
  /** taps required. */
  count?: number;
  /** milliseconds allowed between consecutive taps before the count resets. */
  gapMs?: number;
  /** running tap count (1..count-1) after each tap, then 0 when the window lapses.
      Lets the trigger element answer the taps instead of staying poker-faced. */
  onProgress?: (taps: number) => void;
}

export function useTapSecret(onUnlock: () => void, opts: Options = {}) {
  const count = opts.count ?? 5;
  const gapMs = opts.gapMs ?? 1200;
  const onProgress = opts.onProgress;
  const taps = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    taps.current += 1;
    if (taps.current >= count) {
      taps.current = 0;
      onUnlock();
      return;
    }
    if (onProgress) onProgress(taps.current);
    timer.current = setTimeout(() => {
      taps.current = 0;
      if (onProgress) onProgress(0);
    }, gapMs);
  }, [count, gapMs, onUnlock, onProgress]);
}

export default useTapSecret;
